import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { matchesService } from '@/api/services';
import type { CreateMatchEventRequest, MatchEvent } from '@/types';
import { queryKeys } from './useApi';

type QueueOp =
  | { kind: 'add'; tempId: string; data: CreateMatchEventRequest }
  | { kind: 'update'; eventId: string; data: Partial<CreateMatchEventRequest> }
  | { kind: 'delete'; eventId: string };

const storageKey = (matchId: string) => `live-queue:${matchId}`;
const RETRY_MS = 5000;

const load = (matchId: string): QueueOp[] => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(matchId)) ?? '[]') as QueueOp[];
  } catch {
    return [];
  }
};

const isNetworkError = (err: unknown) => axios.isAxiosError(err) && !err.response;

export const isTempEventId = (id: string) => id.startsWith('tmp-');

/**
 * Очередь событий ведения матча: сначала в localStorage, затем на сервер по порядку.
 * При обрыве сети операции ждут и повторяются; temp-id заменяются на серверные.
 */
export const useLiveEventQueue = (matchId: string, onRejected?: (err: unknown) => void) => {
  const qc = useQueryClient();
  const [queue, setQueue] = useState<QueueOp[]>(() => load(matchId));
  const queueRef = useRef(queue);
  const flushing = useRef(false);
  const inFlight = useRef<QueueOp | null>(null);
  /** tempId → id на сервере: шторка «Кто забил?» может держать temp-id уже отправленного события. */
  const idMap = useRef(new Map<string, string>());
  const resolveId = (id: string) => idMap.current.get(id) ?? id;

  const commit = useCallback(
    (next: QueueOp[]) => {
      queueRef.current = next;
      localStorage.setItem(storageKey(matchId), JSON.stringify(next));
      setQueue(next);
    },
    [matchId],
  );

  const flush = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    try {
      while (queueRef.current.length > 0) {
        const op = queueRef.current[0];
        // Очередь могла измениться за время запроса — убираем именно отправленную операцию.
        const dropSent = (map?: (o: QueueOp) => QueueOp) => {
          const rest = queueRef.current.filter((o) => o !== op);
          commit(map ? rest.map(map) : rest);
        };
        inFlight.current = op;
        try {
          if (op.kind === 'add') {
            const created = await matchesService.addEvent(matchId, op.data);
            idMap.current.set(op.tempId, created.id);
            dropSent((o) => (o.kind !== 'add' && o.eventId === op.tempId ? { ...o, eventId: created.id } : o));
          } else if (isTempEventId(op.eventId)) {
            // Создание этого события было отклонено сервером — менять нечего.
            dropSent();
          } else if (op.kind === 'update') {
            await matchesService.updateEvent(matchId, op.eventId, op.data);
            dropSent();
          } else {
            await matchesService.deleteEvent(matchId, op.eventId);
            dropSent();
          }
        } catch (err) {
          if (isNetworkError(err)) return;
          dropSent();
          onRejected?.(err);
        } finally {
          inFlight.current = null;
        }
      }
      qc.invalidateQueries({ queryKey: queryKeys.matches.events(matchId) });
      qc.invalidateQueries({ queryKey: queryKeys.matches.detail(matchId) });
    } finally {
      flushing.current = false;
    }
  }, [commit, matchId, onRejected, qc]);

  useEffect(() => {
    void flush();
    const onOnline = () => void flush();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => queueRef.current.length && void flush(), RETRY_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [flush]);

  const add = useCallback(
    (data: CreateMatchEventRequest): string => {
      const tempId = `tmp-${crypto.randomUUID()}`;
      commit([...queueRef.current, { kind: 'add', tempId, data }]);
      void flush();
      return tempId;
    },
    [commit, flush],
  );

  const update = useCallback(
    (id: string, data: Partial<CreateMatchEventRequest>) => {
      const eventId = resolveId(id);
      const pendingAdd = queueRef.current.find((o) => o.kind === 'add' && o.tempId === eventId && o !== inFlight.current);
      if (pendingAdd && pendingAdd.kind === 'add') {
        commit(queueRef.current.map((o) => (o === pendingAdd ? { ...pendingAdd, data: { ...pendingAdd.data, ...data } } : o)));
      } else {
        commit([...queueRef.current, { kind: 'update', eventId, data }]);
      }
      void flush();
    },
    [commit, flush],
  );

  const remove = useCallback(
    (id: string) => {
      const eventId = resolveId(id);
      const pendingAdd = queueRef.current.some((o) => o.kind === 'add' && o.tempId === eventId && o !== inFlight.current);
      if (pendingAdd) {
        commit(queueRef.current.filter((o) => !(o.kind === 'add' && o.tempId === eventId) && !('eventId' in o && o.eventId === eventId)));
      } else {
        commit([...queueRef.current, { kind: 'delete', eventId }]);
      }
      void flush();
    },
    [commit, flush],
  );

  /** События сервера с учётом ещё не отправленных операций. */
  const merge = useCallback(
    (serverEvents: MatchEvent[]): MatchEvent[] => {
      const deleted = new Set(queue.filter((o) => o.kind === 'delete').map((o) => (o as { eventId: string }).eventId));
      const updates = new Map<string, Partial<CreateMatchEventRequest>>();
      queue.forEach((o) => o.kind === 'update' && updates.set(o.eventId, { ...updates.get(o.eventId), ...o.data }));
      const pending: MatchEvent[] = queue
        .filter((o): o is Extract<QueueOp, { kind: 'add' }> => o.kind === 'add')
        .map((o) => ({ id: o.tempId, ...o.data }));
      return [...serverEvents.filter((e) => !deleted.has(e.id)).map((e) => ({ ...e, ...updates.get(e.id) })), ...pending];
    },
    [queue],
  );

  return { pending: queue.length, add, update, remove, merge, flush };
};
